import { useState } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";

const S = {
  tools: { es: "Herramientas Interactivas", en: "Interactive Tools" },
  waterTitle: { es: "Calculadora de Agua por Familia", en: "Family Water Calculator" },
  people: { es: "Personas en el hogar", en: "People in household" },
  days: { es: "Días de reserva", en: "Days of reserve" },
  level: { es: "Nivel de uso", en: "Usage level" },
  levelMin: { es: "Mínimo — 1 gal/día (beber y cocinar)", en: "Minimum — 1 gal/day (drink & cook)" },
  levelStd: { es: "Recomendado — 3 gal/día (higiene incluida)", en: "Recommended — 3 gal/day (with hygiene)" },
  levelFull: { es: "Confort — 5 gal/día", en: "Comfort — 5 gal/day" },
  need: { es: "Necesita almacenar", en: "You need to store" },
  gallons: { es: "galones", en: "gallons" },
  liters: { es: "litros", en: "liters" },
  bottles5: { es: "botellones de 5 galones", en: "5-gallon jugs" },
  waterNote: { es: "FEMA y CDC recomiendan una reserva de 10 a 14 días para Puerto Rico.", en: "FEMA and CDC recommend a 10 to 14 day reserve for Puerto Rico." },
  chlorTitle: { es: "Desinfección de Cisterna con Cloro", en: "Cistern Chlorine Disinfection" },
  capacity: { es: "Capacidad de la cisterna (galones)", en: "Cistern capacity (gallons)" },
  shock: { es: "Cloro sin perfume (5-6%) para choque de desinfección", en: "Unscented bleach (5-6%) for disinfection shock" },
  cups: { es: "tazas", en: "cups" },
  shockNote: {
    es: "Aplique, deje reposar 12-24 horas y drene antes de consumo. Tratamiento rutinario para beber: 8 gotas por galón (2 por litro), espere 30 minutos.",
    en: "Apply, let sit 12-24 hours and drain before drinking use. Routine drinking treatment: 8 drops per gallon (2 per liter), wait 30 minutes.",
  },
  chlorDisclaimer: { es: "Siga siempre las instrucciones del Departamento de Salud y la etiqueta del producto.", en: "Always follow Department of Health instructions and the product label." },
  wattTitle: { es: "Calculadora de Vatios para Planta Eléctrica", en: "Generator Wattage Calculator" },
  select: { es: "Seleccione los equipos que necesita encender a la vez", en: "Select the equipment you need to power at once" },
  total: { es: "Consumo estimado", en: "Estimated load" },
  margin: { es: "Con margen de seguridad (25%)", en: "With safety margin (25%)" },
  recommend: { es: "Planta recomendada", en: "Recommended generator" },
  wattNote: {
    es: "Nevera, bomba y aire acondicionado necesitan 2-3 veces más vatios al arrancar. Encienda primero el equipo más grande.",
    en: "Fridge, pump and A/C need 2-3x more watts at startup. Start the largest appliance first.",
  },
};

const APPLIANCES = [
  { id: "nevera", w: 700, es: "Nevera / refrigerador", en: "Refrigerator" },
  { id: "freezer", w: 500, es: "Freezer", en: "Freezer" },
  { id: "abanico", w: 75, es: "Abanico", en: "Fan" },
  { id: "tv", w: 150, es: "Televisor", en: "TV" },
  { id: "led", w: 50, es: "5 bombillas LED", en: "5 LED bulbs" },
  { id: "celulares", w: 20, es: "Cargadores de celular", en: "Phone chargers" },
  { id: "laptop", w: 60, es: "Laptop", en: "Laptop" },
  { id: "cpap", w: 60, es: "Máquina CPAP", en: "CPAP machine" },
  { id: "oxigeno", w: 350, es: "Concentrador de oxígeno", en: "Oxygen concentrator" },
  { id: "microondas", w: 1000, es: "Microondas", en: "Microwave" },
  { id: "cafetera", w: 900, es: "Cafetera", en: "Coffee maker" },
  { id: "bomba", w: 800, es: "Bomba de cisterna", en: "Cistern pump" },
  { id: "lavadora", w: 500, es: "Lavadora", en: "Washing machine" },
  { id: "ac", w: 1200, es: "Aire acondicionado de ventana", en: "Window A/C" },
];

const t = (o, lang) => o[lang] ?? o.es;

const inputCls =
  "w-full rounded-md border-2 border-slate-300 bg-white px-4 py-2.5 text-base font-semibold text-slate-900 focus:border-amber-600 focus:outline-none";

function Card({ icon, title, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-slate-300 bg-white p-6 sm:p-8">
      <h4 className="flex items-center gap-2.5 font-display text-lg sm:text-xl font-bold text-slate-900 mb-5">
        <span className="flex items-center justify-center w-9 h-9 rounded-md bg-[#1C2B4A] text-white">
          <Icon name={icon} className="w-4.5 h-4.5 w-[18px] h-[18px]" />
        </span>
        {title}
      </h4>
      {children}
    </div>
  );
}

function WaterCalculator() {
  const { lang } = useLang();
  const [people, setPeople] = useState(4);
  const [days, setDays] = useState(10);
  const [level, setLevel] = useState(3);
  const gallons = people * days * level;
  return (
    <Card testId="calc-water" icon="Droplets" title={t(S.waterTitle, lang)}>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.people, lang)}</span>
          <input data-testid="water-people-input" type="number" min="1" max="30" value={people}
            onChange={(e) => setPeople(Math.max(1, Number(e.target.value) || 1))} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.days, lang)}</span>
          <input data-testid="water-days-input" type="number" min="1" max="60" value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.level, lang)}</span>
          <select data-testid="water-level-select" value={level} onChange={(e) => setLevel(Number(e.target.value))} className={inputCls}>
            <option value={1}>{t(S.levelMin, lang)}</option>
            <option value={3}>{t(S.levelStd, lang)}</option>
            <option value={5}>{t(S.levelFull, lang)}</option>
          </select>
        </label>
      </div>
      <div data-testid="water-result" className="mt-5 rounded-md bg-[#F8FAFC] border border-slate-200 p-5">
        <p className="text-sm font-bold text-slate-600">{t(S.need, lang)}</p>
        <p className="mt-1 font-display text-4xl font-black tracking-tight text-[#1C2B4A]">
          {gallons.toLocaleString()} <span className="text-lg text-slate-500">{t(S.gallons, lang)}</span>
        </p>
        <p className="mt-2 text-sm text-slate-600">
          ≈ {Math.round(gallons * 3.785).toLocaleString()} {t(S.liters, lang)} · {Math.ceil(gallons / 5)} {t(S.bottles5, lang)}
        </p>
      </div>
      <p className="mt-3 text-xs text-slate-500">{t(S.waterNote, lang)}</p>
    </Card>
  );
}

function ChlorineCalculator() {
  const { lang } = useLang();
  const [capacity, setCapacity] = useState(600);
  const cups = (capacity / 100) * 4;
  return (
    <Card testId="calc-chlorine" icon="Droplets" title={t(S.chlorTitle, lang)}>
      <label className="block max-w-xs">
        <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.capacity, lang)}</span>
        <input data-testid="chlorine-input" type="number" min="50" max="50000" step="50" value={capacity}
          onChange={(e) => setCapacity(Math.max(0, Number(e.target.value) || 0))} className={inputCls} />
      </label>
      <div data-testid="chlorine-result" className="mt-5 rounded-md bg-[#F8FAFC] border border-slate-200 p-5">
        <p className="text-sm font-bold text-slate-600">{t(S.shock, lang)}</p>
        <p className="mt-1 font-display text-4xl font-black tracking-tight text-[#1C2B4A]">
          {cups.toFixed(1)} <span className="text-lg text-slate-500">{t(S.cups, lang)}</span>
          <span className="ml-3 text-xl font-bold text-slate-500">≈ {(cups * 0.2366).toFixed(2)} L</span>
        </p>
      </div>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{t(S.shockNote, lang)}</p>
      <p className="mt-2 text-xs text-slate-500">{t(S.chlorDisclaimer, lang)}</p>
    </Card>
  );
}

export function WaterCalculators() {
  const { lang } = useLang();
  return (
    <section data-testid="water-calculators" className="mt-4">
      <h3 className="mb-5 flex items-center gap-2.5 font-display text-xl sm:text-2xl font-bold text-slate-900">
        <Icon name="Gauge" className="w-6 h-6 text-amber-700" /> {t(S.tools, lang)}
      </h3>
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        <WaterCalculator />
        <ChlorineCalculator />
      </div>
    </section>
  );
}

export function WattCalculator() {
  const { lang } = useLang();
  const [selected, setSelected] = useState(["nevera", "abanico", "led", "celulares"]);
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const total = APPLIANCES.filter((a) => selected.includes(a.id)).reduce((sum, a) => sum + a.w, 0);
  const withMargin = Math.round(total * 1.25);
  const rec =
    withMargin <= 1000 ? "1,000 – 2,000 W" :
    withMargin <= 2500 ? "3,000 – 3,500 W" :
    withMargin <= 4500 ? "5,000 – 6,500 W" : "7,500 W +";

  return (
    <section data-testid="watt-calculator" className="mt-4">
      <h3 className="mb-5 flex items-center gap-2.5 font-display text-xl sm:text-2xl font-bold text-slate-900">
        <Icon name="Gauge" className="w-6 h-6 text-amber-700" /> {t(S.tools, lang)}
      </h3>
      <Card testId="calc-watt" icon="Zap" title={t(S.wattTitle, lang)}>
        <p className="mb-4 text-sm text-slate-600">{t(S.select, lang)}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {APPLIANCES.map((a) => {
            const on = selected.includes(a.id);
            return (
              <button
                key={a.id}
                data-testid={`watt-item-${a.id}`}
                onClick={() => toggle(a.id)}
                aria-pressed={on}
                className={`flex items-center justify-between gap-3 rounded-md border-2 px-4 py-3 text-left text-sm font-semibold transition-colors duration-150 active:scale-[0.99] ${
                  on ? "border-amber-600 bg-amber-50 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span className={`flex h-5 w-5 items-center justify-center rounded border-2 ${on ? "border-green-700 bg-green-700 text-white" : "border-slate-300"}`}>
                    {on && <Icon name="CheckCircle2" className="w-3.5 h-3.5" strokeWidth={3} />}
                  </span>
                  {lang === "es" ? a.es : a.en}
                </span>
                <span className="font-mono-pr text-xs text-slate-500">{a.w} W</span>
              </button>
            );
          })}
        </div>
        <div data-testid="watt-result" className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4">
            <p className="text-xs font-bold text-slate-500">{t(S.total, lang)}</p>
            <p data-testid="watt-total" className="mt-1 font-display text-2xl font-black text-slate-900">{total.toLocaleString()} W</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4">
            <p className="text-xs font-bold text-slate-500">{t(S.margin, lang)}</p>
            <p data-testid="watt-margin" className="mt-1 font-display text-2xl font-black text-slate-900">{withMargin.toLocaleString()} W</p>
          </div>
          <div className="rounded-md border border-amber-600 bg-amber-50 p-4">
            <p className="text-xs font-bold text-amber-800">{t(S.recommend, lang)}</p>
            <p data-testid="watt-recommendation" className="mt-1 font-display text-2xl font-black text-amber-900">{rec}</p>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">{t(S.wattNote, lang)}</p>
      </Card>
    </section>
  );
}
